import React, { useEffect, useMemo, useRef, useState } from "react";
import { PORTFOLIO } from "../assets";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

function BlurUpImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  const base = src.replace(".webp", "");
  return (
    <>
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center blurup-bg"
        style={{ backgroundImage: `url(${base}-blur.webp)` }}
      />
      <img
        src={`${base}-800.webp`}
        srcSet={`${base}-800.webp 800w, ${base}-1400.webp 1400w`}
        sizes="(max-width: 768px) 60vw, 50vw"
        alt={alt}
        loading="lazy"
        decoding="async"
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        ref={(el) => {
          if (el && el.complete && el.naturalWidth > 0 && !loaded) setLoaded(true);
        }}
        onLoad={() => setLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover img-hover img-blurup select-none ${loaded ? "loaded" : ""}`}
      />
    </>
  );
}

const FILTERS = [
  { key: "all", labelKey: "all" },
  { key: "fight", labelKey: "fight" },
  { key: "portrait", labelKey: "portrait" },
  { key: "bts", labelKey: "bts" },
];

export default function Portfolio({ t }) {
  const [active, setActive] = useState("all");
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const touchX = useRef(null);

  const items = useMemo(
    () => (active === "all" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === active)),
    [active]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (lightboxIdx === null) return;
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, items.length]);

  return (
    <section id="work" data-testid="portfolio-section" className="relative bg-ink py-24 md:py-36 overflow-hidden">
      <div className="px-6 md:px-12 lg:px-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16 md:mb-24">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="block h-px w-10 bg-leaf"></span>
              <p className="text-[11px] tracking-[0.4em] uppercase text-ash">{t.portfolio.eyebrow}</p>
            </div>
            <h2
              data-testid="portfolio-title"
              className="font-serif font-light text-bone text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight"
            >
              {t.portfolio.title}
              <br />
              <span className="italic">{t.portfolio.title2}</span>
            </h2>
          </div>

          <div className="flex flex-wrap gap-2" role="tablist">
            {FILTERS.map((f) => {
              const isActive = active === f.key;
              return (
                <button
                  key={f.key}
                  data-testid={`portfolio-filter-${f.key}`}
                  onClick={() => setActive(f.key)}
                  className={`text-[11px] tracking-[0.32em] uppercase px-5 py-3 border transition-all duration-500 ${
                    isActive
                      ? "border-leaf text-bone bg-leaf/15"
                      : "border-line text-ash hover:text-bone hover:border-moss"
                  }`}
                >
                  {t.portfolio[f.labelKey]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Asymmetrical masonry */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {items.map((item, i) => {
            const layout =
              item.span === "tall"
                ? "md:col-span-5 aspect-[3/4]"
                : item.span === "wide"
                ? "md:col-span-7 aspect-[4/3]"
                : "md:col-span-6 aspect-square";
            return (
              <button
                key={item.id}
                data-testid={`portfolio-item-${item.id}`}
                onClick={() => setLightboxIdx(i)}
                className={`relative group overflow-hidden img-hover-wrap text-left ${layout}`}
              >
                <BlurUpImage src={item.src} alt={item.title} />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />

                {/* FL watermark */}
                <img
                  src="/fl-logo.png"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute top-3 right-3 h-7 w-7 md:h-8 md:w-8 rounded-full opacity-40 pointer-events-none select-none z-[4]"
                />

                {/* Big typographic overlay */}
                <p className="absolute -bottom-3 left-2 right-2 font-display text-[18vw] md:text-[10vw] leading-none uppercase tracking-tighter text-bone/[0.08] select-none pointer-events-none">
                  {item.overlay}
                </p>

                <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between z-[3]">
                  <div>
                    <p className="text-[10px] tracking-[0.4em] uppercase text-bone/70 mb-1">
                      {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                    </p>
                    <p className="font-serif text-2xl md:text-3xl text-bone leading-tight">{item.title}</p>
                  </div>
                  <span className="text-[10px] tracking-[0.32em] uppercase text-bone/70 border border-bone/30 px-3 py-1.5">
                    {t.portfolio[item.category === "bts" ? "bts" : item.category]}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          data-testid="lightbox"
          className="lightbox"
          onClick={() => setLightboxIdx(null)}
          onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchX.current;
            touchX.current = null;
            if (Math.abs(dx) < 50) return;
            setLightboxIdx((i) => (dx < 0 ? (i + 1) % items.length : (i - 1 + items.length) % items.length));
          }}
          role="dialog"
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightboxIdx(null)}
            className="absolute top-6 right-6 text-bone/80 hover:text-bone p-2"
            aria-label="Close"
          >
            <X size={22} strokeWidth={1.5} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIdx((i) => (i - 1 + items.length) % items.length);
            }}
            className="absolute left-4 md:left-8 text-bone/70 hover:text-bone p-3"
            aria-label="Previous"
          >
            <ChevronLeft size={28} strokeWidth={1.2} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIdx((i) => (i + 1) % items.length);
            }}
            className="absolute right-4 md:right-8 text-bone/70 hover:text-bone p-3"
            aria-label="Next"
          >
            <ChevronRight size={28} strokeWidth={1.2} />
          </button>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${items[lightboxIdx].src.replace(".webp", "")}-1400.webp`}
              srcSet={`${items[lightboxIdx].src.replace(".webp", "")}-800.webp 800w, ${items[lightboxIdx].src.replace(".webp", "")}-1400.webp 1400w, ${items[lightboxIdx].src} 1920w`}
              sizes="(max-width: 768px) 70vw, 92vw"
              alt={items[lightboxIdx].title}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              className="max-h-[88vh] max-w-[92vw] object-contain select-none"
            />
            <img
              src="/fl-logo.png"
              alt=""
              aria-hidden="true"
              className="absolute bottom-4 right-4 h-9 w-9 md:h-11 md:w-11 rounded-full opacity-45 pointer-events-none select-none"
            />
          </div>
          <div className="absolute bottom-6 left-0 right-0 text-center">
            <p className="font-serif text-bone text-2xl">{items[lightboxIdx].title}</p>
            <p className="text-[10px] tracking-[0.4em] uppercase text-ash mt-2">
              {t.portfolio[items[lightboxIdx].category === "bts" ? "bts" : items[lightboxIdx].category]}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
